// Port of PROTO code.html (BRIEF P4): sticky stacking cards. Shared
// PAGE_CSS rules (.prose, .narrow, .wrap, .section, the Opener lede) already
// live in ../../styles/site.css — only `.cd-cards`/`.cd-card` are
// page-specific and live in CodePage.css.
import { useRef, type ReactNode } from 'react';
import { Opener } from '../Opener.js';
import { gsap, reduceMotion, useGsapContext } from '../motion.js';
import './CodePage.css';

interface Card {
  title: string;
  body: ReactNode;
}

const CARDS: Card[] = [
  {
    title: 'Discord is indispensable',
    body: (
      <>
        The experience of playing Naval Action improves greatly through good collaboration. It is a splendid sight
        to behold ships of the line maneuvering in perfect formation. To achieve such noble coordination, the use
        of <a href="https://discord.com/">Discord</a> is indispensable.
      </>
    ),
  },
  {
    title: 'Uphold our reputation',
    body: 'Statements and actions that reflect poorly on our clan will not be tolerated. Respect all others, kindness goes a long way.',
  },
  {
    title: 'Participating in Port Battles',
    body: 'In port battles and other big conflicts, effective coordination and communication are key. There’s usually a fleet commander who calls the shots, and it’s important to follow their instructions without hesitation. Remember, leading in battle is no easy task, so show respect for the commander’s role and decisions. During the heat of the moment, it’s not the time for discussions—focus on executing the plan. After the battle, we can all come together to review and discuss how to improve for next time.',
  },
  {
    title: '(Voice) Recordings of battles',
    body: (
      <>
        Recording a battle can be a valuable tool for evaluation, reflection, and learning. However, it’s
        important to notify all participants about recordings being made and to{' '}
        <strong>ask for consent beforehand</strong>. This not only respects everyone’s privacy but also fosters
        trust and transparency, creating a positive and cooperative environment for everyone involved. Be mindful
        that not everyone is comfortable with having their voice recorded or shared publicly on the internet.
      </>
    ),
  },
  {
    title: 'Respectful Language',
    body: 'We all have different standards. For some, swearing is normalized and maybe even considered to be a part of gaming culture. While we don’t want to dictate what people can or can’t say, we would prefer to avoid strong language that could be perceived as harsh, abrasive, offensive, inappropriate, or provocative. We don’t expect anyone to be perfect—none of us are. We all occasionally let something slip, but let’s strive not to make the use of strong language a regular habit.',
  },
  {
    title: 'Keep each other accountable',
    body: 'To honor our pirate code, we hold each other accountable with respect and maturity. If areas for improvement are highlighted, there’s no need to take offense—it’s part of helping each other grow.',
  },
];

export function CodePage() {
  const ref = useRef<HTMLDivElement | null>(null);

  // Port of PROTO build.py code.html's scroll-scrub script: as the next
  // card arrives, only the previous card's scale shrinks (transform, never
  // its background/opacity — build-plan rule 5, "backgrounds never fade")
  // while its children's opacity fades. Scoped so leaving and returning to
  // this page never leaves a stray ScrollTrigger (build-plan R2).
  useGsapContext(
    ref,
    () => {
      if (reduceMotion) return;
      const cards = gsap.utils.toArray<HTMLElement>('.cd-card', ref.current ?? undefined);
      cards.forEach((card, i, all) => {
        if (i === all.length - 1) return;
        const scrollTrigger = { trigger: all[i + 1], start: 'top 85%', end: 'top 25%', scrub: true };
        gsap.to(card, { scale: 0.94, ease: 'none', scrollTrigger });
        gsap.to(Array.from(card.children), { opacity: 0.4, ease: 'none', scrollTrigger });
      });
    },
    [],
  );

  return (
    <div className="cd-page" ref={ref}>
      <Opener
        lines={['Pirating code', 'of honor']}
        lede="Rules may bend and break, hence we don’t do rules. Yet the pirate’s code is ever honored with steadfast resolve."
      />
      <section className="wrap section">
        <div className="cd-cards">
          {CARDS.map((card, i) => (
            <article className="cd-card" style={{ '--i': i } as React.CSSProperties} key={card.title}>
              <h2>{card.title}</h2>
              <p>{card.body}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
